import React, { useState, useEffect, useCallback } from 'react';
import Navigation from './components/Navigation';
import MixAndMatchBuilder from './components/MixAndMatchBuilder';
import EducationModule from './components/EducationModule';
import ChatBot from './components/ChatBot';
import CheckoutModal from './components/CheckoutModal';
import VerificationModal from './components/VerificationModal';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import SpecMatrix from './components/SpecMatrix';
import DeployedSection from './components/DeployedSection';
import HeritageStory from './components/HeritageStory';
import CustomerDemoSection from './components/CustomerDemoSection';
import DealerLocator from './components/DealerLocator';
import NewsPage from './components/NewsPage';
import Footer from './components/Footer';
import CatalogPage from './components/CatalogPage';
import WholesalePage from './components/WholesalePage';
import LegalPage from './components/LegalPage';
import AgeGate from './components/AgeGate';
import TrustStrip from './components/TrustStrip';
import AdminLoginForm from './components/admin/AdminLoginForm';
import AdminDashboard from './components/admin/AdminDashboard';
import RetailerApplicationForm from './components/RetailerApplicationForm';
import RetailerLoginModal from './components/RetailerLoginModal';
import PromoBanner from './components/PromoBanner';
import FeatureProductPage from './components/FeatureProductPage';
import { PRODUCTS } from './constants';
import { getFeatureProfile } from './data/featureProfiles';
import { Product, OrderItem, BusinessDetails, AdminUser } from './types';
import { onAuthStateChanged, getCurrentAdmin, getCurrentRetailer, signOutRetailer, RetailerUser } from './services/authService';

const PATH_TO_VIEW: Record<string, string> = {
  '/': 'home',
  '/apply': 'apply',
  '/shop': 'prototype',
  '/prototype': 'prototype', // legacy alias — old links keep working
  '/specs': 'specs',
  '/education': 'education',
  '/news': 'news',
  '/locator': 'locator',
  '/catalog': 'catalog',
  '/wholesale': 'wholesale',
  '/legal': 'legal',
  '/admin': 'admin',
};

const VIEW_TO_PATH: Record<string, string> = {
  home: '/',
  apply: '/apply',
  prototype: '/shop',
  specs: '/specs',
  education: '/education',
  news: '/news',
  locator: '/locator',
  catalog: '/catalog',
  wholesale: '/wholesale',
  legal: '/legal',
  admin: '/admin',
};

// Feature profile pages live at /machetes/<slug> and are encoded in view state
// as 'machete:<slug>' so the existing string-based view routing keeps working.
const MACHETE_PATH_PREFIX = '/machetes/';

const pathToView = (pathname: string): string => {
  if (pathname.startsWith(MACHETE_PATH_PREFIX)) {
    const slug = pathname.slice(MACHETE_PATH_PREFIX.length).replace(/\/+$/, '');
    if (getFeatureProfile(slug)) return `machete:${slug}`;
  }
  return PATH_TO_VIEW[pathname] ?? 'home';
};

const viewToPath = (view: string): string => {
  if (view.startsWith('machete:')) return `${MACHETE_PATH_PREFIX}${view.slice('machete:'.length)}`;
  return VIEW_TO_PATH[view] ?? '/';
};

const getInitialView = (): string => {
  if (typeof window === 'undefined') return 'home';
  return pathToView(window.location.pathname);
};

const VIEW_TITLES: Record<string, string> = {
  home: 'The Machete Man | Premium Colombian Machetes',
  prototype: 'Shop Machetes Online in Canada | The Machete Man',
  specs: 'Machete Blade Specifications | The Machete Man',
  education: 'Machete Guides & Safety | The Machete Man',
  awareness: 'Machete Guides & Safety | The Machete Man',
  warranty: 'Warranty & Returns | The Machete Man',
  locator: 'Where to Buy | The Machete Man',
  news: 'News | The Machete Man',
  catalog: 'Product Catalog | The Machete Man',
  wholesale: 'Wholesale Machetes Canada | The Machete Man',
  legal: 'Legal & Policies | The Machete Man',
  apply: 'Retailer Application | The Machete Man',
  admin: 'Portal | The Machete Man',
};

const VIEW_DESCRIPTIONS: Record<string, string> = {
  home: 'Premium Colombian machetes, drop-forged by Incolma since 1960. Authentic full-tang SAE 1075 high-carbon steel blades, shipped from our Scarborough, Ontario warehouse. Retail and wholesale.',
  prototype: 'Buy authentic Incolma machetes online — 15 drop-forged profiles from $19.99 CAD. Peinilla, Rula, Bananera, Rozador and more, shipped across Canada. 18+, ID on delivery.',
  specs: 'Full engineering specifications for Incolma machetes: SAE 1075 carbon steel, hardness, blade geometry, and an interactive anatomy guide.',
  education: 'Machete safety, garden and landscaping uses, camping and bushcraft, and rust prevention — practical guides from The Machete Man.',
  awareness: 'Machete safety, garden and landscaping uses, camping and bushcraft, and rust prevention — practical guides from The Machete Man.',
  warranty: 'Warranty coverage and 14-day returns for Incolma machetes: what is covered, what is not, and how to make a claim.',
  locator: 'Where to buy authentic Incolma machetes in Canada — buy direct online today, or pick up at our Scarborough warehouse by appointment.',
  news: 'Product launches, logistics updates, and retailer market intelligence from The Machete Man — Canada\'s exclusive Incolma distributor.',
  catalog: 'The complete Incolma product catalog with specifications and photography — downloadable PDF available.',
  wholesale: 'Wholesale machetes for shops, garden centres, and outfitters. Canada\'s exclusive Incolma distributor — 12-unit minimum, mix and match.',
  legal: 'Terms of sale, age and compliance policy, shipping and returns, and privacy policy for The Machete Man (Machetes and More Ltd.).',
  apply: 'Apply for a wholesale account with The Machete Man — Canada\'s exclusive Incolma machete distributor. Approval usually within one business day.',
  admin: 'Staff portal.',
};

const App: React.FC = () => {
  const [currentView, setViewState] = useState<string>(getInitialView);

  const setView = useCallback((view: string) => {
    setViewState(view);
    const path = viewToPath(view);
    if (typeof window !== 'undefined' && window.location.pathname !== path) {
      window.history.pushState({ view }, '', path);
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setViewState(pathToView(window.location.pathname));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Per-view SEO: title, meta description, canonical URL, robots, and
  // product structured data on the shop view.
  useEffect(() => {
    const featureProfile = currentView.startsWith('machete:')
      ? getFeatureProfile(currentView.slice('machete:'.length))
      : undefined;

    document.title = featureProfile
      ? `${featureProfile.name} — Uses, Care & Safety | The Machete Man`
      : VIEW_TITLES[currentView] ?? VIEW_TITLES.home;

    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) {
      canonical.href = `https://themacheteman.com${viewToPath(currentView)}`;
    }

    const desc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (desc) {
      desc.content = featureProfile
        ? featureProfile.seoDescription
        : VIEW_DESCRIPTIONS[currentView] ?? VIEW_DESCRIPTIONS.home;
    }

    // Keep the staff portal out of the index
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (currentView === 'admin') {
      if (!robots) {
        robots = document.createElement('meta');
        robots.name = 'robots';
        document.head.appendChild(robots);
      }
      robots.content = 'noindex, nofollow';
    } else if (robots) {
      robots.remove();
    }

    // Structured data: Product list on the shop view, single Product on
    // feature profile pages.
    const LD_ID = 'view-products-jsonld';
    const existing = document.getElementById(LD_ID);
    if (featureProfile) {
      const prod = PRODUCTS.find((p) => p.id === featureProfile.productIds[0]);
      const productLd = prod && {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: prod.name,
        sku: prod.sku,
        brand: { '@type': 'Brand', name: 'Incolma' },
        image: `https://themacheteman.com${featureProfile.image}`,
        description: featureProfile.seoDescription,
        offers: {
          '@type': 'Offer',
          price: (prod.retailPrice ?? prod.msrp).toFixed(2),
          priceCurrency: 'CAD',
          availability: prod.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
          url: `https://themacheteman.com${viewToPath(currentView)}`,
        },
      };
      if (productLd) {
        const script = existing ?? document.createElement('script');
        script.id = LD_ID;
        (script as HTMLScriptElement).type = 'application/ld+json';
        script.textContent = JSON.stringify(productLd);
        if (!existing) document.head.appendChild(script);
      }
    } else if (currentView === 'prototype') {
      const itemList = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'The Machete Man — Incolma Collection',
        itemListElement: PRODUCTS.map((prod, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'Product',
            name: prod.name,
            sku: prod.sku,
            brand: { '@type': 'Brand', name: prod.category === 'Okapi Ratchet' ? 'Okapi' : 'Incolma' },
            image: `https://themacheteman.com${prod.image}`,
            description: prod.description,
            offers: {
              '@type': 'Offer',
              price: (prod.retailPrice ?? prod.msrp).toFixed(2),
              priceCurrency: 'CAD',
              availability: prod.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
              url: 'https://themacheteman.com/shop',
            },
          },
        })),
      };
      const script = existing ?? document.createElement('script');
      script.id = LD_ID;
      (script as HTMLScriptElement).type = 'application/ld+json';
      script.textContent = JSON.stringify(itemList);
      if (!existing) document.head.appendChild(script);
    } else if (existing) {
      existing.remove();
    }
  }, [currentView]);

  const [cartCount, setCartCount] = useState(0);
  const [cartSubtotal, setCartSubtotal] = useState(0);
  const [cartItems, setCartItems] = useState<OrderItem[]>([]);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isVerificationOpen, setIsVerificationOpen] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [businessDetails, setBusinessDetails] = useState<BusinessDetails | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [retailerUser, setRetailerUser] = useState<RetailerUser | null>(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  // Products from constants (no localStorage caching to avoid stale data)
  const [products] = useState<Product[]>(PRODUCTS);

  // 18+ entry gate — remembered per device. Government photo ID is verified at delivery.
  const [isAgeVerified, setIsAgeVerified] = useState<boolean>(
    () => typeof window !== 'undefined' && window.localStorage.getItem('mm_age_verified') === 'true'
  );
  const handleAgeConfirm = useCallback(() => {
    if (typeof window !== 'undefined') window.localStorage.setItem('mm_age_verified', 'true');
    setIsAgeVerified(true);
  }, []);

  // Check for existing retailer session on app load
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(async (user) => {
      if (user) {
        // Check if this is a retailer (not admin)
        const retailer = await getCurrentRetailer(user);
        if (retailer) {
          setRetailerUser(retailer);
          setIsVerified(true);
          setBusinessDetails({ name: retailer.businessName, taxId: '' });
        }
      } else {
        // User logged out
        setRetailerUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Check for existing admin session when entering admin view
  useEffect(() => {
    if (currentView === 'admin' && !adminUser) {
      setIsCheckingAuth(true);
      const unsubscribe = onAuthStateChanged(async (user) => {
        if (user) {
          const admin = await getCurrentAdmin(user);
          if (admin) {
            setAdminUser(admin);
          }
        }
        setIsCheckingAuth(false);
      });
      return () => unsubscribe();
    }
  }, [currentView, adminUser]);

  // Handle retailer login success
  const handleRetailerLoginSuccess = useCallback((retailer: RetailerUser) => {
    setRetailerUser(retailer);
    setIsVerified(true);
    setBusinessDetails({ name: retailer.businessName, taxId: '' });
    setIsLoginModalOpen(false);
  }, []);

  // Handle retailer logout
  const handleRetailerLogout = useCallback(async () => {
    await signOutRetailer();
    setRetailerUser(null);
    setIsVerified(false);
    setBusinessDetails(null);
  }, []);

  const handleCheckoutRequest = () => {
    // Retail customers check out without an account; wholesale accounts are
    // already verified by the time they reach here. Either way, open checkout.
    setIsCheckoutOpen(true);
  };

  // Stable identity so MixAndMatchBuilder's cart effect doesn't re-fire every
  // render (it lists onCartUpdate as a dependency).
  const handleCartUpdate = useCallback((count: number, total: number, items: OrderItem[]) => {
    setCartCount(count);
    setCartSubtotal(total);
    setCartItems(items);
  }, []);

  const handleApplyClick = () => {
    setView('apply');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLoginClick = () => {
    setIsLoginModalOpen(true);
  };

  const handleVerificationComplete = (details: { businessName: string; taxId: string }) => {
    setBusinessDetails({ name: details.businessName, taxId: details.taxId });
    setIsVerified(true);
    setIsVerificationOpen(false);
    setIsCheckoutOpen(true);
  };

  const renderView = () => {
    if (currentView.startsWith('machete:')) {
      const profile = getFeatureProfile(currentView.slice('machete:'.length));
      if (profile) {
        return (
          <>
            <FeatureProductPage
              profile={profile}
              onShopClick={() => setView('prototype')}
              onWholesaleClick={() => setView('wholesale')}
              onViewProfile={(slug) => setView(`machete:${slug}`)}
            />
            <Footer setView={setView} />
          </>
        );
      }
    }
    switch(currentView) {
      case 'home':
        return (
          <div className="bg-paper">
            <HeroSection onBuildClick={() => setView('prototype')} onWholesaleClick={() => setView('wholesale')} onLoginClick={handleLoginClick} />
            <TrustStrip />
            <AboutSection />
            <CustomerDemoSection />
            <HeritageStory />
            <DeployedSection />
            <Footer setView={setView} />
          </div>
        );
      case 'prototype':
        return (
           <>
             <MixAndMatchBuilder
               products={products}
               onCartUpdate={handleCartUpdate}
               openCheckout={handleCheckoutRequest}
               isVerified={isVerified}
               onLoginClick={handleLoginClick}
               retailerUser={retailerUser}
               onLogout={handleRetailerLogout}
               onViewProfile={(slug) => setView(`machete:${slug}`)}
             />
             <Footer setView={setView} />
           </>
        );
      case 'specs':
        return (
          <>
            <SpecMatrix products={products} />
            <Footer setView={setView} />
           </>
        );
      case 'education':
        return (
           <>
             <EducationModule initialTab="awareness" />
             <Footer setView={setView} />
           </>
        );
      case 'awareness':
        return (
           <>
             <EducationModule initialTab="awareness" />
             <Footer setView={setView} />
           </>
        );
      case 'warranty':
        return (
           <>
             <EducationModule initialTab="warranty" />
             <Footer setView={setView} />
           </>
        );
      case 'locator':
        return (
           <>
             <DealerLocator />
             <Footer setView={setView} />
           </>
        );
      case 'news':
        return (
            <>
              <NewsPage />
              <Footer setView={setView} />
            </>
        );
      case 'catalog':
        return (
            <>
              <CatalogPage />
              <Footer setView={setView} />
            </>
        );
      case 'wholesale':
        return (
            <>
              <WholesalePage
                retailerUser={retailerUser}
                onLoginClick={handleLoginClick}
                onApplyClick={handleApplyClick}
                onShopClick={() => setView('prototype')}
                onLogout={handleRetailerLogout}
              />
              <Footer setView={setView} />
            </>
        );
      case 'legal':
        return (
            <>
              <LegalPage />
              <Footer setView={setView} />
            </>
        );
      case 'apply':
        return (
          <RetailerApplicationForm onBack={() => setView('home')} />
        );
      case 'admin':
        if (isCheckingAuth) {
          return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
              <div className="text-white">Loading...</div>
            </div>
          );
        }
        if (adminUser) {
          return (
            <AdminDashboard
              admin={adminUser}
              onLogout={() => {
                setAdminUser(null);
                setView('home');
              }}
            />
          );
        }
        return (
          <AdminLoginForm
            onLoginSuccess={(admin) => setAdminUser(admin)}
            onBackToSite={() => setView('home')}
          />
        );
      default:
        return (
           <>
             <HeroSection onBuildClick={() => setView('prototype')} onWholesaleClick={() => setView('wholesale')} onLoginClick={handleLoginClick} />
             <Footer setView={setView} />
           </>
        );
    }
  };

  const hideNavigation = currentView === 'admin' || currentView === 'apply';

  return (
    <div className="min-h-screen bg-paper font-sans text-ink-900 selection:bg-ember selection:text-paper-warm">
      {!isAgeVerified && currentView !== 'admin' && <AgeGate onConfirm={handleAgeConfirm} />}
      {currentView !== 'admin' && <PromoBanner />}
      {!hideNavigation && (
        <Navigation
          currentView={currentView}
          setView={setView}
          cartCount={cartCount}
          onCartClick={handleCheckoutRequest}
        />
      )}
      <main>
        {renderView()}
      </main>
      {/* Floating WhatsApp button removed 2026-09-23 (owner request) — WhatsApp
          contact remains in the nav, footer, and checkout. */}
      {!hideNavigation && <ChatBot />}

      <CheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        cartCount={cartCount}
        subtotal={cartSubtotal}
        businessDetails={businessDetails}
        cartItems={cartItems}
        products={products}
        isVerified={isVerified}
      />

      <VerificationModal
        isOpen={isVerificationOpen}
        onClose={() => setIsVerificationOpen(false)}
        onVerify={handleVerificationComplete}
      />

      <RetailerLoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={handleRetailerLoginSuccess}
        onApplyClick={handleApplyClick}
      />
    </div>
  );
};

export default App;
